import { useMemo } from "react";
import { calculateDisciplineTouchStats } from "../utils/touchStats";

const FLAIR = { from: "#F06292", to: "#FF8A65", solid: "#FF7043" };

/**
 * TouchStatsSummary — admin-facing "tale of the tape" for a discipline: total bouts,
 * total touches, and top fighters (by total and by average) and fights ranked by
 * tap-counter activity.
 *
 * Props:
 *   bouts: [...all bouts for this discipline]
 *   affiliations: { [fighterName]: schoolName } — optional, shown after each name
 */
function FighterName({ name, affiliation, className = "text-sm font-semibold text-gray-200 truncate", style }) {
  return (
    <span className={className} style={style}>
      {name}
      {affiliation && <span className="text-xs font-normal text-gray-500/60 ml-1.5">{affiliation}</span>}
    </span>
  );
}

function FighterBlock({ name, affiliation, color }) {
  return (
    <div className="flex flex-col items-center text-center min-w-0">
      <span className="text-sm font-semibold truncate w-full" style={{ color }}>{name}</span>
      {affiliation && <span className="text-[10px] font-normal text-gray-500/60 truncate w-full">{affiliation}</span>}
    </div>
  );
}

export default function TouchStatsSummary({ bouts, affiliations = {} }) {
  const stats = useMemo(() => calculateDisciplineTouchStats(bouts), [bouts]);

  if (stats.totalBouts === 0) {
    return (
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-8 text-center">
        <div className="font-display text-sm font-bold text-gray-400 mb-1">No touch data yet</div>
        <p className="text-gray-600 text-xs max-w-sm mx-auto">
          Touch counts are recorded automatically from the judge tap counter as rounds are scored.
          Check back once some bouts have been scored.
        </p>
      </div>
    );
  }

  const maxFighterTouches = Math.max(...stats.topFighters.map((f) => f.touches), 1);
  const maxFighterAvg = Math.max(...stats.topFightersByAvg.map((f) => f.avgTouchesPerRound), 1);
  const maxFighterRatio = Math.max(...stats.topFightersByRatio.map((f) => f.ratio), 1);

  return (
    <div>
      {/* Hero stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "BOUTS SCORED", value: stats.totalBouts },
          { label: "TOTAL TOUCHES", value: stats.totalTouches },
          { label: "AVG / BOUT", value: stats.avgTouchesPerBout },
          { label: "AVG / ROUND", value: stats.avgTouchesPerRound },
        ].map((tile) => (
          <div key={tile.label} className="relative overflow-hidden rounded-xl border border-dark-500 p-4" style={{ background: "linear-gradient(160deg, #1a1414, #111)" }}>
            <div className="font-display text-[10px] font-bold text-gray-500 tracking-widest mb-1">{tile.label}</div>
            <div className="font-display text-3xl font-extrabold" style={{ backgroundImage: `linear-gradient(135deg, ${FLAIR.from}, ${FLAIR.to})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              {tile.value}
            </div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5 items-stretch">
        <div className="flex flex-col justify-between gap-5">
          {/* Top fighters by total */}
          <div className="bg-dark-700 border border-dark-500 rounded-xl p-4">
            <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-4">TOP 5 FIGHTERS — TOTAL TOUCHES</div>
            <div className="flex flex-col gap-3">
              {stats.topFighters.map((f, i) => (
                <div key={f.name} className="flex items-center gap-3">
                  <span className="w-6 text-center font-display text-sm font-extrabold text-gray-500">{`#${i + 1}`}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between mb-1 gap-2">
                      <FighterName name={f.name} affiliation={affiliations[f.name]} />
                      <span className="font-display text-sm font-extrabold shrink-0" style={{ color: FLAIR.solid }}>{f.touches}</span>
                    </div>
                    <div className="h-2 rounded-full bg-dark-900 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.max(6, (f.touches / maxFighterTouches) * 100)}%`, background: `linear-gradient(90deg, ${FLAIR.from}, ${FLAIR.to})` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top fighters by average */}
          <div className="bg-dark-700 border border-dark-500 rounded-xl p-4">
            <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-4">TOP 5 FIGHTERS — AVG TOUCHES / ROUND</div>
            <div className="flex flex-col gap-3">
              {stats.topFightersByAvg.map((f, i) => (
                <div key={f.name} className="flex items-center gap-3">
                  <span className="w-6 text-center font-display text-sm font-extrabold text-gray-500">{`#${i + 1}`}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between mb-1 gap-2">
                      <FighterName name={f.name} affiliation={affiliations[f.name]} />
                      <span className="font-display text-sm font-extrabold shrink-0" style={{ color: FLAIR.solid }}>{f.avgTouchesPerRound}</span>
                    </div>
                    <div className="h-2 rounded-full bg-dark-900 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.max(6, (f.avgTouchesPerRound / maxFighterAvg) * 100)}%`, background: `linear-gradient(90deg, ${FLAIR.from}, ${FLAIR.to})` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Top fighters by touch ratio */}
          <div className="bg-dark-700 border border-dark-500 rounded-xl p-4">
            <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-4">TOP 5 FIGHTERS — TOUCH RATIO (LANDED : RECEIVED)</div>
            <div className="flex flex-col gap-3">
              {stats.topFightersByRatio.map((f, i) => (
                <div key={f.name} className="flex items-center gap-3">
                  <span className="w-6 text-center font-display text-sm font-extrabold text-gray-500">{`#${i + 1}`}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between mb-1 gap-2">
                      <FighterName name={f.name} affiliation={affiliations[f.name]} />
                      <span className="font-display text-sm font-extrabold shrink-0" style={{ color: FLAIR.solid }}>{f.ratio}×</span>
                    </div>
                    <div className="h-2 rounded-full bg-dark-900 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${Math.max(6, (f.ratio / maxFighterRatio) * 100)}%`, background: `linear-gradient(90deg, ${FLAIR.from}, ${FLAIR.to})` }} />
                    </div>
                  </div>
                </div>
              ))}
              {stats.topFightersByRatio.length === 0 && <p className="text-gray-600 text-xs">Not enough data yet.</p>}
            </div>
          </div>
        </div>

        {/* Top fights */}
        <div className="bg-dark-700 border border-dark-500 rounded-xl p-4">
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-4">TOP 5 FIGHTS — AVG TOUCHES / ROUND</div>
          <div className="flex flex-col gap-2.5">
            {stats.topFights.map((f, i) => (
              <div key={f.boutId} className="rounded-lg border border-dark-500 p-3" style={{ background: "#0e0e0e" }}>
                <div className="text-center mb-1.5">
                  <span className="font-display text-lg font-extrabold text-gray-500">{`#${i + 1}`}</span>
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
                  <FighterBlock name={f.fighterBlue} affiliation={affiliations[f.fighterBlue]} color="#64B5F6" />
                  <span className="text-gray-700 text-sm mt-0.5">vs</span>
                  <FighterBlock name={f.fighterRed} affiliation={affiliations[f.fighterRed]} color="#EF9A9A" />
                </div>
                <div className="flex flex-col items-center mt-2">
                  <span className="font-display text-lg font-extrabold" style={{ color: FLAIR.solid }}>{f.avgTouchesPerRound}</span>
                  <span className="text-[10px] text-gray-600">touches/rnd</span>
                </div>
                <div className="text-center text-[10px] text-gray-600 mt-1">
                  {f.totalTouches} touches over {f.rounds} round{f.rounds !== 1 ? "s" : ""}
                  {f.poolName ? ` • ${f.poolName}` : f.phase === "elimination" ? " • Elimination" : ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
